import EmberService, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import {
	NavObjectType,
	NavRoute,
} from '@databox/ember-integrations/lib/components/Navigation/navigation.contract';
import type {
	NavigationImperativeApi,
	NavTarget,
} from '@databox/ember-integrations/lib/components/Navigation/navigation.contract';
import { SettingsTab } from '@databox/types/domains/settings/settings.enums';

import type { Service } from 'webapp-types';
import { ActivityLogObjectType } from 'webapp/enums/activity-log';
import { SupportChannel } from 'webapp/enums/ai-chat';
import { BillingFeature, SelectSourceOptionType, Theme } from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import { TrackingEvent } from 'webapp/enums/tracking';
import { UserSettingsKey } from 'webapp/enums/user-settings';
import { getTs, isAlive, MD_BREAKPOINT, redirect } from 'webapp/utils/common';

import OnboardingPopupNewNavigationComponent from 'webapp/components/onboarding/popup-new-navigation';

import { Step } from 'webapp/services/onboarding';

const RELEASE_NEW_NAVIGATION_TS = getTs('2026-08-13 09:00:00');
const AI_LANDING_TIMEOUT_MS = 500;
const DEVELOPERS_URL = 'https://developers.databox.com';

export default class NavigationMainService extends EmberService {
	@service('account') declare accountService: Service.Account;
	@service('account-progress') declare accountProgressService: Service.AccountProgress;
	@service('ai-chat') declare aiChatService: Service.AIChat;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('app-notifications') declare appNotificationsService: Service.AppNotifications;
	@service('billing') declare billingService: Service.Billing;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('product-updates') declare productUpdatesService: Service.ProductUpdates;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('request') declare requestService: Service.Request;
	@service('store') declare storeService: Service.Store;
	@service('user-settings') declare userSettingsService: Service.UserSettings;
	@service('onboarding') declare onboardingService: Service.Onboarding;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('model-manager/spaces') declare mmSpacesService: Service.ModelManager.Spaces;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;
	@service('wizard') declare wizardService: Service.Wizard;
	@service('logger') declare loggerService: Service.Logger;
	@service('popup') declare popupService: Service.Popup;
	@service('automated-popup-queue')
	declare automatedPopupQueueService: Service.AutomatedPopupQueue;

	@tracked isNarrowView = false;
	@tracked isNavigationMainExpanded = false;

	// Handed over by the navigation component once React has mounted (onInit).
	navigationImperativeApi: NavigationImperativeApi | null = null;

	// Where the user was headed at the latest Genie icon click; null while no landing is resolving.
	private aiLandingStartRoute: string | null = null;

	runInitialChecks() {
		this.checkNarrowView();

		// intial approximation to avoid react flicker
		this.isNavigationMainExpanded =
			this.userSettingsService.preferMainNavigationExpanded && !this.isNarrowView;
	}

	get skipNavigationMain() {
		const route = this.navigationService.activatedRoute;

		return (
			!this.navigationService.initialRequestsDone ||
			!this.navigationService.isMainApp ||
			route.startsWith('boards.tabs') ||
			route.startsWith('boards.view') ||
			route.startsWith('boards.loops.loop') ||
			route.startsWith('reports.view') ||
			(route.startsWith('wizard') && this.wizardService.isWideView) ||
			(this.navigationService.isOnboarding &&
				[Step.WELCOME, Step.FINAL].includes(this.onboardingService.currentStep))
		);
	}

	checkNarrowView() {
		if (isAlive(this)) {
			this.isNarrowView = window.innerWidth < MD_BREAKPOINT;
		}
	}

	// ------ New navigation ------

	// Single source of truth linking every `NavRoute` (the React sidebar's rail/panel
	// vocabulary) to the Ember route names it owns. `Record<NavRoute, ...>` forces every
	// enum member to be handled.
	//
	//  - getRouteFromNavRoute (forward): returns the FIRST route in the array — the default
	//    target a rail/panel click transitions to. An empty array = no route of its own; the
	//    item is handled by a bespoke case in `handleNavTarget` (e.g. CLIENT_CREATE opens a popup).
	//  - activatedNavRoute (reverse): matches the current route (by exact name or as a prefix
	//    of a nested route) and picks the LONGEST match, so a page resolves to its specific
	//    panel item rather than its broader rail landing. Panel items are listed BEFORE their
	//    rail landing so they win exact-length ties (e.g. `genie` → AI_NEW_CHAT, not AI).
	//
	// Routes with no sidebar home (settings, wizard, onboarding, mobile, designer's parents,
	// public pages, etc.) are intentionally absent — activatedNavRoute returns null for them.
	routeConfig: Partial<Record<NavRoute, string[]>> = {
		[NavRoute.HOME]: ['index', 'home'],

		[NavRoute.AI_NEW_CHAT]: ['genie.chat.index'],
		[NavRoute.AI_ARTIFACTS]: ['artifacts'],
		[NavRoute.AI_ROUTINES]: ['routines.index'],
		// Both AI settings items resolve directly in `activatedNavRoute`.
		// 'routines' is listed here as well as under AI_ROUTINES above, and the longest-match rule splits
		// them: 'routines.index' (the list) wins for the list route, while 'routines.detail' / 'routines.run'
		// match only this shorter 'routines' and resolve to AI. That upholds the React contract's invariant
		// that a routine is never reported while AI_ROUTINES is the active route, which would highlight the
		// Routines panel item and a routine row at once. 'genie' stays first so the forward lookup
		// (getRouteFromNavRoute returns the FIRST entry) still lands on genie.
		[NavRoute.AI]: ['genie', 'routines'],

		[NavRoute.BI_METRICS]: ['metrics'],
		[NavRoute.BI_DATABOARDS]: ['boards'],
		[NavRoute.BI_REPORTS]: [
			'reports.index',
			'reports.schedules',
			'reports.archive',
			'reports.account-templates',
			'reports.public-templates',
		],
		[NavRoute.BI_GOALS]: ['goals'],
		[NavRoute.BI_SPACES]: ['teams'],
		[NavRoute.BI]: ['metrics'],

		[NavRoute.AGENTS]: ['agents'],

		[NavRoute.DATA_SOURCES]: ['datamanager.connected'],
		[NavRoute.DATA_DATASETS]: ['datamanager.datasets', 'datamanager.connected.datasets'],
		[NavRoute.DATA_MERGED_DATASETS]: [
			'datamanager.merged-datasets',
			'datamanager.create-new-merge',
		],
		[NavRoute.DATA_CONNECTIONS]: ['datamanager.connections'],
		[NavRoute.DATA_INTEGRATIONS]: [
			'datamanager.available',
			'datamanager.auto-connect',
			'datamanager.external-install',
		],
		[NavRoute.DATA_METRIC_LIBRARY]: ['metric-library'],
		[NavRoute.DATA]: ['datamanager'],

		[NavRoute.CLIENT]: ['clients'],
		[NavRoute.CLIENT_OVERVIEW]: ['clients'],
		[NavRoute.CLIENT_CREATE]: [], // opens the create-client popup (no route) — handled in onNavigate
	};

	gatedNavRoutes: Partial<Record<NavRoute, BillingFeature>> = {
		[NavRoute.AI_ROUTINES]: BillingFeature.AI_ROUTINES,
		[NavRoute.AI_ARTIFACTS]: BillingFeature.AI_ARTIFACTS,
	};

	get activatedNavRoute(): NavRoute | null {
		const activatedRoute = String(this.navigationService.activatedRoute);

		// every tab resolves to Customize, as both settings routes did before the popup
		if (this.navigationService.activeCustomizeTab) {
			return NavRoute.AI_CUSTOMIZE;
		}

		let bestNavRoute: NavRoute | null = null;
		let bestLength = 0;

		for (const [navRoute, routes] of Object.entries(this.routeConfig) as [
			NavRoute,
			string[],
		][]) {
			for (const route of routes) {
				const matches = activatedRoute === route || activatedRoute.startsWith(`${route}.`);

				// Strict `>` keeps the earliest-declared NavRoute on ties, so panel items
				// (listed first) win over their rail landing for a shared route.
				if (matches && route.length > bestLength) {
					bestNavRoute = navRoute;
					bestLength = route.length;
				}
			}
		}

		// A merged dataset opens on the same datamanager.connected.datasets.* routes as a regular dataset,
		// so the route name alone cannot tell them apart: the open source decides.
		if (bestNavRoute === NavRoute.DATA_DATASETS && this.isMergedDatasetOpen) {
			return NavRoute.DATA_MERGED_DATASETS;
		}

		return bestNavRoute;
	}

	private get isMergedDatasetOpen(): boolean {
		const id = this.navigationService.activatedRouteParams.space_access_id;
		return (
			typeof id === 'string' &&
			this.mmSourcesService.findRecord(id)?.selectedSourceOption ===
				SelectSourceOptionType.MERGED
		);
	}

	// The open chat thread, for the AI panel to mark that row as selected. Gated on the AI landing route, which
	// covers exactly the genie thread routes — `genie.chat.index` (New Chat) resolves to AI_NEW_CHAT instead. That
	// upholds the React contract's invariant that a thread is never reported while AI_NEW_CHAT is the active route,
	// which would highlight "New chat" and a thread row at once. Defence in depth: the genie route also clears the
	// id on exit, so a leftover from an earlier visit can't leak in either.
	// `isGenie` narrows the shared AI landing route to this subsystem: NavRoute.AI now also covers a specific
	// routine, and the ids are cleared in the exiting route's resetController (end of transition) while
	// activatedRoute flips in routeWillChange (start), so without it a genie→routines transition would report
	// the still-set thread id on a routine route. Both predicates read activatedRoute, so they flip in step.
	get activeThreadId(): string | null {
		return this.activatedNavRoute === NavRoute.AI && this.navigationService.isGenie
			? this.aiChatService.urlThreadId
			: null;
	}

	// The open routine, for the AI panel to mark that row as selected. Gated on the same AI landing route as
	// activeThreadId above, which the routeConfig now resolves the routine detail/run routes to while the
	// list stays AI_ROUTINES — so a routine is never reported while the Routines panel item is itself active.
	// Selection is by routine, not by run: every run of a routine keeps the same row selected.
	get activeRoutineId(): string | null {
		return this.activatedNavRoute === NavRoute.AI && this.navigationService.isRoutines
			? this.aiChatService.urlRoutineId
			: null;
	}

	// Reopened by the Genie icon while it's the only chat. isTrial stands in for feature_onboarded,
	// which BE doesn't expose yet: https://app.asana.com/1/7582066485656/task/1218491190957189
	get resumeThreadId(): string | null {
		return this.billingService.isTrial && this.currentUserService.isOwner
			? this.userSettingsService.onboardingChatThreadId
			: null;
	}

	expireResumeThread(): void {
		this.userSettingsService.onboardingChatThreadId = null;
	}

	// The webapp doesn't call the AI BFF itself, so React supplies the thread count.
	async openAiLanding(onboardingThreadId: string): Promise<void> {
		const isResolving = this.aiLandingStartRoute !== null;

		// A repeated click re-arms the pending landing for where it was made.
		this.aiLandingStartRoute = this.navigationService.activatedRoute;

		if (isResolving) {
			return;
		}

		try {
			const summary = await this.fetchAiThreadsSummary();

			// activatedRoute flips as a navigation starts; a newer one wins: no landing, no expiry.
			if (this.navigationService.activatedRoute !== this.aiLandingStartRoute) {
				return;
			}

			if (summary?.totalItems === 1 && summary.firstThreadId === onboardingThreadId) {
				this.navigationService.transitionToRoute('genie.chat.thread', onboardingThreadId);

				return;
			}

			if (summary) {
				this.expireResumeThread();
			}

			this.navigationService.transitionToRoute(this.getRouteFromNavRoute(NavRoute.AI));
		} finally {
			this.aiLandingStartRoute = null;
		}
	}

	// A lookup without a result counts as an unavailable count: start screen, setting kept.
	private async fetchAiThreadsSummary() {
		let timeoutId: ReturnType<typeof setTimeout> | undefined;
		const timeoutPromise = new Promise<null>((resolve) => {
			timeoutId = setTimeout(() => resolve(null), AI_LANDING_TIMEOUT_MS);
		});

		try {
			return (
				(await Promise.race([
					this.navigationImperativeApi?.getAiThreadsSummary(),
					timeoutPromise,
				])) ?? null
			);
		} catch (error) {
			this.loggerService.logError('[Nav] getAiThreadsSummary failed', { error });

			return null;
		} finally {
			clearTimeout(timeoutId);
		}
	}

	// Routes the current user must NOT see
	get hideNavRoutes(): NavRoute[] {
		const account = this.accountService;
		const user = this.currentUserService;
		const isViewer = user.isViewer;
		const isOnboarding = this.navigationService.isOnboarding;
		const requestsDone = this.navigationService.initialRequestsDone;
		const isAdmin = user.isAdmin;
		const isClient = account.isClient;
		const isAdminOutsideClientSpace = account.isAdminButNotInClientSpace;
		const isTrial = this.billingService.isTrial;
		// The whole Help sub-popup is hidden for non-admins on white-labeled / demo-style accounts.
		const hideNonWhiteLabeled = account.hideDataboxNotWhiteLabeledContentForNonAdmins;
		const canRequestSetupHelp = !user.isClientUserInPaidClientSpace;

		// Every non-profile (rail/panel) route is hidden during the wizard/onboarding flow, matching
		// the `{{#unless isWizardOrOnboardingRoute}}` guard around the nav in components/navigation/main.
		const isWizardOrOnboarding = this.navigationService.isWizard || isOnboarding;
		const noAiChat = !this.aiChatService.isAiChatAvailable;
		const noSkills = !this.aiChatService.canUseSkills;
		const isEligibleForTeams = requestsDone && !isViewer;

		const hidden: Partial<Record<NavRoute, boolean>> = {
			[NavRoute.HOME]: isWizardOrOnboarding || !user.hasHome,

			// --- AI panel ---
			[NavRoute.AI]: isWizardOrOnboarding || noAiChat,
			[NavRoute.AI_NEW_CHAT]: isWizardOrOnboarding || noAiChat,
			[NavRoute.AI_ARTIFACTS]: isWizardOrOnboarding,
			[NavRoute.AI_ROUTINES]: isWizardOrOnboarding,
			[NavRoute.AI_SKILLS]: isWizardOrOnboarding || noSkills,
			[NavRoute.AI_CUSTOMIZE]: isWizardOrOnboarding,

			// --- BI panel ---
			[NavRoute.BI]: isWizardOrOnboarding, // same as Databoards - show rail item for viewer
			[NavRoute.BI_METRICS]: isWizardOrOnboarding || isViewer,
			[NavRoute.BI_DATABOARDS]: isWizardOrOnboarding,
			[NavRoute.BI_REPORTS]: isWizardOrOnboarding || isViewer,
			[NavRoute.BI_GOALS]: isWizardOrOnboarding || isViewer,
			[NavRoute.BI_SPACES]: isWizardOrOnboarding || !isEligibleForTeams,

			// --- Agents ---
			[NavRoute.AGENTS]: isWizardOrOnboarding || isViewer,

			// --- Data Manager panel ---
			[NavRoute.DATA]: isWizardOrOnboarding || isViewer,
			[NavRoute.DATA_SOURCES]: isWizardOrOnboarding,
			[NavRoute.DATA_DATASETS]: isWizardOrOnboarding,
			[NavRoute.DATA_MERGED_DATASETS]: isWizardOrOnboarding,
			[NavRoute.DATA_CONNECTIONS]: isWizardOrOnboarding,
			[NavRoute.DATA_INTEGRATIONS]: isWizardOrOnboarding,
			[NavRoute.DATA_METRIC_LIBRARY]: isWizardOrOnboarding,
			[NavRoute.DATA_BUILDER]: isWizardOrOnboarding,
			[NavRoute.DATA_API]: isWizardOrOnboarding || hideNonWhiteLabeled,
			[NavRoute.DATA_MCP]: isWizardOrOnboarding || hideNonWhiteLabeled,

			// --- Client ---
			[NavRoute.CLIENT]: isWizardOrOnboarding || isViewer,
			[NavRoute.CLIENT_OVERVIEW]: isWizardOrOnboarding || isViewer,
			[NavRoute.CLIENT_CREATE]: isWizardOrOnboarding || isViewer,

			// Profile
			[NavRoute.PROFILE_MY_PROFILE]: !user.isOnboarded,
			[NavRoute.PROFILE_ACCOUNT_MANAGEMENT]: !(isAdmin && !isOnboarding),
			[NavRoute.PROFILE_INVITE]: isOnboarding,
			[NavRoute.PROFILE_NOTIFICATIONS]: isOnboarding || user.isViewer,

			[NavRoute.PROFILE_MORE]: !((isAdmin || isAdminOutsideClientSpace) && !isOnboarding),
			[NavRoute.PROFILE_USER_MANAGEMENT]: !isAdmin,
			[NavRoute.PROFILE_PLANS]: !isTrial && isClient,
			[NavRoute.PROFILE_BILLING]: isClient,
			[NavRoute.PROFILE_ACTIVITY_LOG]: !(isAdminOutsideClientSpace && !isOnboarding),

			[NavRoute.PROFILE_ACCOUNT_PROGRESS]: !(
				requestsDone &&
				!isOnboarding &&
				!hideNonWhiteLabeled &&
				!account.isDemoAccount &&
				!this.accountProgressService.isAllDone
			),

			[NavRoute.PROFILE_HELP]: !(
				requestsDone &&
				!hideNonWhiteLabeled &&
				!account.isDemoAccount &&
				!(isOnboarding && this.onboardingService.isUserOnboarding)
			),
			[NavRoute.PROFILE_EXPLORE_DEMO]: hideNonWhiteLabeled || !account.isDemoButtonShown,
			[NavRoute.PROFILE_BOOK_A_CALL]: !this.aiChatService.canBookACall,
			// Onboarding is itself a Genie chat and has no launcher, so a side panel would open over it.
			[NavRoute.PROFILE_START_CHAT]:
				hideNonWhiteLabeled ||
				isOnboarding ||
				!(this.aiChatService.hasSupportChat && canRequestSetupHelp),
			[NavRoute.PROFILE_KNOWLEDGE_BASE]: hideNonWhiteLabeled,
			[NavRoute.PROFILE_SYSTEM_STATUS]: hideNonWhiteLabeled || !(!isClient && isAdmin),

			[NavRoute.PROFILE_PRODUCT_UPDATES]: !(
				((account.whiteLabelingLoaded && !account.whiteLabelingEnabled) ||
					(!isClient && isAdmin)) &&
				!isOnboarding
			),
			[NavRoute.PROFILE_DARK_THEME]: isOnboarding,
		};

		return (Object.keys(hidden) as NavRoute[]).filter((route) => hidden[route]);
	}

	getRouteFromNavRoute(navRoute: NavRoute): string {
		return this.routeConfig[navRoute]?.[0] ?? '';
	}

	handleNavTarget(navTarget: NavTarget) {
		// console.debug('[navigation-main] onNavigate', navTarget);

		let isUnhandled = false;

		if (typeof navTarget === 'string') {
			const target: NavRoute = navTarget;

			const gatedFeature = this.gatedNavRoutes[target];

			if (gatedFeature && !this.billingService.checkIfCan(gatedFeature)) {
				this.billingService.handleLimitations(gatedFeature);

				return;
			}

			if (target === NavRoute.AI && this.resumeThreadId) {
				void this.openAiLanding(this.resumeThreadId);

				return;
			}

			switch (target) {
				// Rail landing + panel routes: resolved through `routeConfig`. Items with no route of
				// their own are handled by the bespoke cases below.
				case NavRoute.HOME:
				case NavRoute.AI:
				case NavRoute.AI_NEW_CHAT:
				case NavRoute.AI_ARTIFACTS:
				case NavRoute.AI_ROUTINES:
				case NavRoute.BI:
				case NavRoute.BI_METRICS:
				case NavRoute.BI_DATABOARDS:
				case NavRoute.BI_REPORTS:
				case NavRoute.BI_GOALS:
				case NavRoute.BI_SPACES:
				case NavRoute.AGENTS:
				case NavRoute.DATA:
				case NavRoute.DATA_SOURCES:
				case NavRoute.DATA_DATASETS:
				case NavRoute.DATA_MERGED_DATASETS:
				case NavRoute.DATA_CONNECTIONS:
				case NavRoute.DATA_INTEGRATIONS:
				case NavRoute.DATA_METRIC_LIBRARY:
				case NavRoute.CLIENT:
				case NavRoute.CLIENT_OVERVIEW: {
					const route = this.getRouteFromNavRoute(target);
					if (route) {
						// console.log('[navigation-main] transition', route);
						if (route.startsWith('clients') && !this.accountService.isAgency) {
							return;
						}
						this.navigationService.transitionToRoute(route);
					} else {
						isUnhandled = true;
					}
					break;
				}

				// Bespoke handlers (no route, or side effects beyond a transition).
				// Via `onNavigateToSettings` to clear the sticky skills params.
				case NavRoute.AI_CUSTOMIZE:
					this.aiChatService.onNavigateToSettings(SettingsTab.PERSONALIZATION);
					break;
				case NavRoute.AI_SKILLS:
					this.aiChatService.onNavigateToSettings(SettingsTab.SKILLS);
					break;
				case NavRoute.PROFILE_DARK_THEME:
					if (!this.navigationService.isDesigner) {
						const newValue = !this.navigationService.theme.isDark;
						const themeId = newValue ? Theme.DARK : Theme.LIGHT;
						void this.userSettingsService.save(UserSettingsKey.COLOR_MODE, themeId);
						this.navigationService.createTheme(themeId);
						this.eventManagerService.trigger(AppEvent.THEME_CHANGED);
					}
					break;
				case NavRoute.CLIENT_CREATE:
					this.mmSpacesService.openCreateClientPopup('sidebar');
					break;
				case NavRoute.DATA_BUILDER: {
					const customIntegrationsSourceType =
						this.mmSourceTypesService.sourceTypeModels.find(
							(item) => item.isDatasetCustomIntegration,
						)!;
					this.mmSourceTypesService.goToSourceTypeInfo(customIntegrationsSourceType);
					break;
				}
				case NavRoute.DATA_API:
					window.open(DEVELOPERS_URL, '_blank');
					break;
				case NavRoute.DATA_MCP:
					window.open(`${DEVELOPERS_URL}/docs/mcp/overview`, '_blank');
					break;

				// Profile-menu routes, extracted from components/navigation/popup-profile-dropdown{,-more}.
				case NavRoute.PROFILE_MY_PROFILE:
					if (!this.accountService.handleDemo()) {
						this.aiChatService.onNavigateToProfile();
					}
					break;
				case NavRoute.PROFILE_ACCOUNT_MANAGEMENT:
					if (!this.accountService.handleDemo()) {
						this.accountService.openAccountAppCompanyInformation();
					}
					break;
				case NavRoute.PROFILE_INVITE:
					this.mmUsersService.checkToOpenInvitePopup();
					break;
				case NavRoute.PROFILE_NOTIFICATIONS:
					this.transitionFromProfile('settings.notifications');
					break;
				case NavRoute.PROFILE_ACCOUNT_PROGRESS:
					this.transitionFromProfile('settings.level');
					break;
				case NavRoute.PROFILE_PRODUCT_UPDATES:
					this.analyticsService.event(TrackingEvent.PRODUCT_UPDATES_CLICKED);
					this.productUpdatesService.saveProductUpdatesSeen();
					window.open('https://updates.databox.com', '_blank');
					break;
				case NavRoute.PROFILE_LOGOUT:
					redirect('/logout');
					break;
				case NavRoute.PROFILE_USER_MANAGEMENT:
					this.transitionFromProfile('settings.users');
					break;
				case NavRoute.PROFILE_PLANS:
					this.openPlans();
					break;
				case NavRoute.PROFILE_BILLING:
					this.transitionFromProfile('settings.billing');
					break;
				case NavRoute.PROFILE_ACTIVITY_LOG:
					this.transitionFromProfile('settings.logs');
					break;
				case NavRoute.PROFILE_EXPLORE_DEMO:
					void this.accountService.redirectToDemoAccount('help');
					break;
				case NavRoute.PROFILE_BOOK_A_CALL:
					this.analyticsService.event(TrackingEvent.CALL_INITIATED, {
						origin: this.billingService.getOriginForCallInitiated('help'),
					});
					this.accountService.requestSetupHelp();
					break;
				case NavRoute.PROFILE_START_CHAT:
					// On the Genie page or a routine run a Genie user is already in a chat, so nothing opens (see
					// `openSupportChat`)
					if (
						this.navigationService.isGenieChatPage &&
						this.aiChatService.supportChannel === SupportChannel.GENIE
					) {
						break;
					}

					this.analyticsService.event(TrackingEvent.CHAT_INITIATED, {
						origin: this.billingService.getOriginForCallInitiated('help'),
					});
					this.currentUserService.chatWithSupport();
					break;
				case NavRoute.PROFILE_KNOWLEDGE_BASE:
					window.open(this.accountService.shownHelpUrl, '_blank');
					break;
				case NavRoute.PROFILE_SYSTEM_STATUS:
					window.open('https://status.databox.com/', '_blank');
					break;

				// "More" and "Help" are sub-popup containers whose expansion the React nav owns; they
				// are never emitted through onNavigate. Flag as unhandled so a stray emit is logged.
				case NavRoute.PROFILE_MORE:
				case NavRoute.PROFILE_HELP:
					isUnhandled = true;
					break;

				default: {
					// Exhaustiveness guard: a new NavRoute without a case above leaves `target`
					// wider than `never`, failing the build here.
					const _exhaustive: never = target;
					void _exhaustive;
					isUnhandled = true;
					break;
				}
			}
		} else {
			const target: {
				objectType: NavObjectType;
				objectId: number | string;
				// Only AI_ROUTINE carries one — the run its row stands for.
				runId?: string;
			} = navTarget;
			const { objectType, objectId, runId } = target;
			const objectIdString = String(objectId);

			switch (objectType) {
				case NavObjectType.SPACE: {
					const space = this.mmSpacesService.findModel(objectIdString);
					if (space) {
						this.analyticsService.event(TrackingEvent.SWITCH_ACCOUNT, {
							origin: 'popup',
						});
						redirect(space.loginUrl);
					}
					break;
				}
				case NavObjectType.BOARD: {
					const board = this.mmBoardsService.findModel(objectIdString);
					if (board) {
						this.mmBoardsService.openPreview(board, 'sidebar');
					}
					break;
				}
				case NavObjectType.BOARD_LOOP: {
					const loop = this.mmBoardLoopsService.findModel(objectIdString);
					if (loop) {
						this.mmBoardLoopsService.openPreviewPopup(loop, 'sidebar');
					}
					break;
				}
				case NavObjectType.REPORT: {
					const report = this.mmReportsService.findModel(objectIdString);
					if (report) {
						this.mmReportsService.openPreviewPopup(report, 'sidebar');
					}
					break;
				}
				case NavObjectType.AI_THREAD: {
					this.navigationService.transitionToRoute('genie.chat.thread', objectIdString);
					break;
				}
				// The AI panel's ROUTINES rows stand for what a routine produced, so they carry the
				// newest run and land on its thread. Without one — nothing has produced a thread yet,
				// or the run could not be read — the detail page is the fallback.
				case NavObjectType.AI_ROUTINE: {
					if (runId) {
						this.navigationService.transitionToRoute(
							'routines.run',
							objectIdString,
							runId,
						);
					} else {
						this.navigationService.transitionToRoute('routines.detail', objectIdString);
					}
					break;
				}
				default:
					isUnhandled = true;
					break;
			}
		}

		if (isUnhandled) {
			this.loggerService.logError('[Nav] Unhandled NavTarget', { navTarget });
		}
	}

	// Demo-guarded route transition
	private transitionFromProfile(route: string): void {
		if (this.accountService.handleDemo()) {
			return;
		}
		this.navigationService.transitionToRoute(route);
	}

	// "Plans & Add-ons" entry, mirroring CommonUpgradePlanLink used with no `feature`
	private openPlans(): void {
		if (!this.billingService.checkForPermissions()) {
			return;
		}
		this.analyticsService.event(TrackingEvent.UPGRADE_INITIATED, {
			origin: 'profile_dropdown_popup',
			type: this.billingService.getTrackingSubscriptionType(),
		});
		this.navigationService.transitionToRoute('settings.billing.plans');
	}

	getNavObjectTypeFromActivityLogObjectType(type: ActivityLogObjectType): NavObjectType | null {
		switch (type) {
			case ActivityLogObjectType.BOARD:
				return NavObjectType.BOARD;
			case ActivityLogObjectType.BOARD_LOOP:
				return NavObjectType.BOARD_LOOP;
			case ActivityLogObjectType.REPORT:
				return NavObjectType.REPORT;
			default:
				return null;
		}
	}

	// ---- New "onboarding" popup to new navigation for old users ----

	private createOnboardingNewNavigationPopup() {
		return this.popupService.createModal(OnboardingPopupNewNavigationComponent, {
			popupClass: 'popup-type-onboarding-new-navigation',
			closeOnOverlayClick: false,
		});
	}

	addOnboardingNewPopupToAutomatedPopupQueue(): void {
		const { isViewer, isDemo, model: currentUser } = this.currentUserService;
		const userCreatedTS = currentUser?.created;
		const showForOldUsers = !!userCreatedTS && userCreatedTS < RELEASE_NEW_NAVIGATION_TS;

		this.automatedPopupQueueService.queuePopup({
			popup: this.createOnboardingNewNavigationPopup(),
			checkAddConditions: () => {
				return (
					showForOldUsers && !isViewer && !isDemo && !this.navigationService.isMobileApp
				);
			},
			checkStartConditions: () => {
				return (
					!this.userSettingsService.hasSeenOnboardingNewNavigationPopup &&
					window.innerWidth >= MD_BREAKPOINT
				);
			},
			skipCooldown: true,
		});
	}
}

declare module '@ember/service' {
	interface Registry {
		'navigation-main': NavigationMainService;
	}
}
